// Weather Service for Arya Web App
// Integrates with location service to provide automatic weather updates

import locationService from "./locationService";

class WeatherService {
  constructor() {
    this.apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:8080";
    this.currentWeather = null;
    this.weatherCallbacks = [];
    this.updateInterval = null;
    this.isUpdating = false;
  }

  // Get weather for current location
  async getCurrentLocationWeather() {
    try {
      const location = await locationService.getCurrentLocationWithName();
      if (!location) {
        throw new Error("Location not available");
      }

      return await this.getWeatherByLocation(location.name);
    } catch (error) {
      console.error("Failed to get current location weather:", error);
      throw error;
    }
  }

  // Get weather for a specific location
  async getWeatherByLocation(location) {
    try {
      const response = await fetch(
        `${this.apiBaseUrl}/api/v1/weather?location=${encodeURIComponent(
          location
        )}`
      );

      if (!response.ok) {
        throw new Error(`Weather API error: ${response.status}`);
      }

      const weather = await response.json();
      return {
        ...weather,
        location: location,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error("Failed to get weather:", error);
      throw error;
    }
  }

  // Get weather forecast for current location
  async getCurrentLocationForecast(days = 5) {
    try {
      const location = await locationService.getCurrentLocationWithName();
      if (!location) {
        throw new Error("Location not available");
      }

      return await this.getForecastByLocation(location.name, days);
    } catch (error) {
      console.error("Failed to get current location forecast:", error);
      throw error;
    }
  }

  // Get weather forecast for a specific location
  async getForecastByLocation(location, days = 5) {
    try {
      const response = await fetch(
        `${
          this.apiBaseUrl
        }/api/v1/weather/forecast?location=${encodeURIComponent(
          location
        )}&days=${days}`
      );

      if (!response.ok) {
        throw new Error(`Weather forecast API error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Failed to get weather forecast:", error);
      throw error;
    }
  }

  // Get Jarvis-style weather summary for current location
  async getCurrentLocationSummary() {
    try {
      const location = await locationService.getCurrentLocationWithName();
      if (!location) {
        throw new Error("Location not available");
      }

      return await this.getSummaryByLocation(location.name);
    } catch (error) {
      console.error("Failed to get current location summary:", error);
      throw error;
    }
  }

  // Get weather summary for a specific location
  async getSummaryByLocation(location) {
    try {
      const response = await fetch(
        `${
          this.apiBaseUrl
        }/api/v1/weather/summary?location=${encodeURIComponent(location)}`
      );

      if (!response.ok) {
        throw new Error(`Weather summary API error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Failed to get weather summary:", error);
      throw error;
    }
  }

  // Get weather recommendations for current location
  async getCurrentLocationRecommendations() {
    try {
      const location = await locationService.getCurrentLocationWithName();
      if (!location) {
        throw new Error("Location not available");
      }

      return await this.getRecommendationsByLocation(location.name);
    } catch (error) {
      console.error("Failed to get current location recommendations:", error);
      throw error;
    }
  }

  // Get weather recommendations for a specific location
  async getRecommendationsByLocation(location) {
    try {
      const response = await fetch(
        `${
          this.apiBaseUrl
        }/api/v1/weather/recommendations?location=${encodeURIComponent(
          location
        )}`
      );

      if (!response.ok) {
        throw new Error(
          `Weather recommendations API error: ${response.status}`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Failed to get weather recommendations:", error);
      throw error;
    }
  }

  // Subscribe to weather updates
  subscribe(callback) {
    this.weatherCallbacks.push(callback);
    return () => {
      this.weatherCallbacks = this.weatherCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  // Notify all subscribers of weather updates
  notifyCallbacks(weather) {
    this.weatherCallbacks.forEach((callback) => {
      try {
        callback(weather);
      } catch (error) {
        console.error("Weather callback error:", error);
      }
    });
  }

  // Start automatic weather updates
  startAutoUpdates(intervalMinutes = 10) {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }

    // Update immediately
    this.updateWeather();

    // Set up periodic updates
    this.updateInterval = setInterval(() => {
      this.updateWeather();
    }, intervalMinutes * 60 * 1000);

    // Subscribe to location changes
    locationService.subscribe(() => {
      this.updateWeather();
    });
  }

  // Stop automatic weather updates
  stopAutoUpdates() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  // Update weather data
  async updateWeather() {
    if (this.isUpdating) {
      return;
    }

    this.isUpdating = true;
    try {
      const weather = await this.getCurrentLocationWeather();
      this.currentWeather = weather;
      this.notifyCallbacks(weather);
    } catch (error) {
      console.error("Failed to update weather:", error);
    } finally {
      this.isUpdating = false;
    }
  }

  // Get current weather data
  getCurrentWeather() {
    return this.currentWeather;
  }

  // Format temperature for display
  formatTemperature(temp, unit = "C") {
    if (unit === "F") {
      return `${Math.round((temp * 9) / 5 + 32)}°F`;
    }
    return `${Math.round(temp)}°C`;
  }

  // Get weather icon based on description
  getWeatherIcon(description) {
    const desc = description.toLowerCase();
    if (desc.includes("sun") || desc.includes("clear")) {
      return "☀️";
    } else if (desc.includes("cloud")) {
      return "☁️";
    } else if (desc.includes("rain")) {
      return "🌧️";
    } else if (desc.includes("snow")) {
      return "❄️";
    } else if (desc.includes("storm")) {
      return "⛈️";
    } else if (desc.includes("fog") || desc.includes("mist")) {
      return "🌫️";
    } else {
      return "🌤️";
    }
  }

  // Get weather color based on temperature
  getWeatherColor(temp) {
    if (temp < 0) {
      return "#4A90E2"; // Blue for cold
    } else if (temp < 10) {
      return "#7ED321"; // Light blue for cool
    } else if (temp < 20) {
      return "#F5A623"; // Orange for mild
    } else if (temp < 30) {
      return "#D0021B"; // Red for warm
    } else {
      return "#9013FE"; // Purple for hot
    }
  }

  // Initialize weather service
  async initialize() {
    try {
      // Initialize location service first
      await locationService.initialize();

      // Get initial weather
      await this.updateWeather();

      // Start auto updates
      this.startAutoUpdates();

      return true;
    } catch (error) {
      console.error("Failed to initialize weather service:", error);
      return false;
    }
  }
}

// Create singleton instance
const weatherService = new WeatherService();

export default weatherService;
